import React, { useState, useRef, useEffect } from 'react';
import { InboxConversation } from '../hooks';
import { format } from 'timeago.js';
import { Search, ChevronDown, Check, LayoutGrid } from 'lucide-react';
import { LPLogoWName } from '@assets/index';

interface SidebarProps {
    conversations: InboxConversation[];
    selectedId: string | null;
    onSelect: (id: string) => void;
    loading: boolean;
    connectedPages: any[];
    selectedPageId: string | "all";
    onSelectPage: (pageId: string | "all") => void;
    onLoadMore: () => void;
}


const Sidebar: React.FC<SidebarProps> = ({
    conversations,
    selectedId,
    onSelect,
    loading,
    connectedPages,
    selectedPageId,
    onSelectPage,
    onLoadMore
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const filteredConversations = conversations.filter(c =>
        c.user_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const selectedPage = connectedPages.find(p => p.igsid === selectedPageId);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
        // Trigger when 20px from bottom are left
        if (scrollHeight - scrollTop <= clientHeight + 20) {
            onLoadMore();
        }
    };

    return (
        <div className="w-full h-full flex flex-col bg-white">
            {/* Header with Account Switcher */}
            <div className="p-4 border-b border-gray-100 sticky top-0 bg-white z-10">
                <div className="mb-4">
                    {connectedPages.length > 0 ? (
                        <div className="relative" ref={dropdownRef}>
                            <button
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                className="flex items-center gap-2 w-full hover:bg-gray-50 p-2 -ml-2 rounded-lg transition-colors"
                            >
                                {/* Avatar for selector */}
                                {selectedPageId === "all" ? (
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-purple-500 to-pink-500 flex items-center justify-center text-white">
                                        <LayoutGrid size={16} />
                                    </div>
                                ) : (
                                    <img
                                        src={selectedPage?.profile_picture_url || LPLogoWName}
                                        alt="Profile"
                                        className="w-8 h-8 rounded-full border border-gray-200"
                                    />
                                )}

                                <div className="flex-1 text-left overflow-hidden">
                                    <h2 className="text-sm font-bold text-gray-800 truncate">
                                        {selectedPageId === "all" ? "All Accounts" : (selectedPage?.username || "Inbox")}
                                    </h2>
                                    <p className="text-[10px] text-gray-500 truncate">
                                        {selectedPageId === "all" ? "Unified Inbox" : "Instagram Direct"}
                                    </p>
                                </div>
                                <ChevronDown size={16} className={`text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {isDropdownOpen && (
                                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 py-1 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                                    {/* All Accounts Option */}
                                    <button
                                        onClick={() => {
                                            onSelectPage("all");
                                            setIsDropdownOpen(false);
                                        }}
                                        className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 border-b border-gray-50"
                                    >
                                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600">
                                            <LayoutGrid size={16} />
                                        </div>
                                        <span className={`text-sm flex-1 ${selectedPageId === "all" ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                                            All Accounts
                                        </span>
                                        {selectedPageId === "all" && <Check size={16} className="text-blue-500" />}
                                    </button>

                                    {/* Individual Accounts */}
                                    <div className="max-h-60 overflow-y-auto custom-scrollbar">
                                        {connectedPages.map(page => (
                                            <button
                                                key={page.igsid}
                                                onClick={() => {
                                                    onSelectPage(page.igsid);
                                                    setIsDropdownOpen(false);
                                                }}
                                                className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3"
                                            >
                                                <img
                                                    src={page.profile_picture_url}
                                                    alt={page.username}
                                                    className="w-8 h-8 rounded-full border border-gray-100"
                                                />
                                                <span className={`text-sm flex-1 truncate ${page.igsid === selectedPageId ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                                                    {page.username}
                                                </span>
                                                {page.igsid === selectedPageId && (
                                                    <Check size={16} className="text-blue-500" />
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <h2 className="text-xl font-bold text-gray-800">Inbox</h2>
                    )}
                </div>

                {/* Search */}
                <div className="relative group">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 group-focus-within:text-blue-500 transition-colors" />
                    <input
                        type="text"
                        placeholder="Search conversations..."
                        className="w-full pl-10 pr-4 py-2 bg-gray-50/50 border border-gray-100 rounded-xl text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* List */}
            <div
                className="flex-1 overflow-y-auto custom-scrollbar"
                onScroll={onScroll}
            >
                {loading ? (
                    <div className="p-4 flex flex-col gap-4">
                        {[1, 2, 3, 4, 5].map(i => (
                            <div key={i} className="animate-pulse flex gap-3 items-center">
                                <div className="w-12 h-12 bg-gray-100 rounded-full shrink-0"></div>
                                <div className="flex-1 space-y-2 min-w-0">
                                    <div className="h-4 bg-gray-100 rounded w-1/2"></div>
                                    <div className="h-3 bg-gray-50 rounded w-3/4"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : filteredConversations.length === 0 ? (
                    <div className="p-8 text-center flex flex-col items-center justify-center h-full text-gray-400">
                        <Search size={32} className="mb-3 opacity-20" />
                        <p className="text-sm font-medium">No conversations found</p>
                        {searchTerm && <p className="text-xs mt-1 opacity-70">Try a different search term</p>}
                    </div>
                ) : (
                    <div className="flex flex-col">
                        {filteredConversations.map((conv) => {
                            const isSelected = selectedId === conv.id;
                            // Find the page this conversation belongs to (to show small indicator in unified view)
                            const convPage = connectedPages.find(p => p.igsid === conv.page_id);

                            return (
                                <div
                                    key={conv.id}
                                    onClick={() => onSelect(conv.id)}
                                    className={`
                    px-4 py-3 flex items-center gap-3 cursor-pointer transition-all duration-200 border-b border-gray-50/50 hover:bg-gray-50 relative
                    ${isSelected ? 'bg-blue-50/40' : ''}
                  `}
                                >
                                    {isSelected && <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500 rounded-r"></div>}

                                    <div className="relative shrink-0">
                                        <img
                                            src={conv.user_pic || "https://via.placeholder.com/40"}
                                            alt={conv.user_name}
                                            className="w-12 h-12 rounded-full object-cover border border-gray-100 bg-white"
                                        />

                                        {/* Page Indicator (only in Unified View) */}
                                        {selectedPageId === "all" && convPage && (
                                            <img
                                                src={convPage.profile_picture_url}
                                                className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white"
                                                title={`Via @${convPage.username}`}
                                            />
                                        )}
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-baseline mb-0.5">
                                            <h3 className={`text-sm truncate pr-2 ${isSelected || conv.unread_count > 0 ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                                                {conv.user_name}
                                            </h3>
                                            {conv.last_message_at && (
                                                <span className="text-[10px] text-gray-400 whitespace-nowrap shrink-0">
                                                    {format(conv.last_message_at.toDate())}
                                                </span>
                                            )}
                                        </div>

                                        <div className="flex justify-between items-center">
                                            <p className={`text-xs truncate max-w-[85%] ${isSelected || conv.unread_count > 0 ? 'text-gray-600 font-medium' : 'text-gray-400'}`}>
                                                {conv.last_message || "Started a conversation"}
                                            </p>

                                            {conv.unread_count > 0 && (
                                                <span className="flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold text-white bg-blue-500 rounded-full px-1 shrink-0 ml-2">
                                                    {conv.unread_count}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Sidebar;
